import { useI18n } from '@i18n';
import { Box, TextField } from '@mui/material';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import { clamp } from 'lodash';
import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { ICONS } from '@/assets/icons';
import { PIconButton } from '@/components/atoms/IconButton/PIconButton';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { ICON_SIZE_MAP } from '@/config';

function lastPageIndex(count: number, rowsPerPage: number): number {
  if (rowsPerPage === -1) return 0;
  return Math.max(0, Math.ceil(count / rowsPerPage) - 1);
}

/**
 * Custom `ActionsComponent` for `TablePaginationStandard`: adds a "jump to
 * page" input between the prev/next arrows, alongside the total page count.
 * The row-range text ("51-75 of 168") stays MUI's own `labelDisplayedRows` —
 * this only replaces the arrows region.
 */
export const TablePaginationActions = ({
  className,
  count,
  page,
  rowsPerPage,
  onPageChange,
}: TablePaginationActionsProps) => {
  const { t } = useI18n();
  const maxPageIndex = lastPageIndex(count, rowsPerPage);
  const inputWidthCh = String(maxPageIndex + 1).length + 4;
  const [draft, setDraft] = useState(String(page + 1));

  useEffect(() => {
    setDraft(String(page + 1));
  }, [page]);

  const commit = useCallback(() => {
    const parsed = Number.parseInt(draft, 10);
    const nextPage = Number.isNaN(parsed) ? page : clamp(parsed, 1, maxPageIndex + 1) - 1;
    setDraft(String(nextPage + 1));
    if (nextPage !== page) onPageChange(null, nextPage);
  }, [draft, page, maxPageIndex, onPageChange]);

  const handleDraftChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setDraft(event.target.value.replace(/\D/g, '')),
    [],
  );

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.currentTarget.blur();
  }, []);

  const handlePreviousPage = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => onPageChange(event, page - 1),
    [onPageChange, page],
  );

  const handleNextPage = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => onPageChange(event, page + 1),
    [onPageChange, page],
  );

  return (
    <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
      <PIconButton
        aria-label={t('General_Table-PreviousPage')}
        icon={<Iconify icon={ICONS.CHEVRON_LEFT} width={ICON_SIZE_MAP.smPlus} height={ICON_SIZE_MAP.smPlus} />}
        variant="ghost"
        size="small"
        disabled={page <= 0}
        onClick={handlePreviousPage}
      />
      <TextField
        size="small"
        value={draft}
        onChange={handleDraftChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        inputProps={{ 'aria-label': t('General_Table-JumpToPage'), inputMode: 'numeric' }}
        sx={{
          width: `${inputWidthCh}ch`,
          '& .MuiOutlinedInput-input': { textAlign: 'center', py: 0.5, px: 1 },
        }}
      />
      <PTypography variant="body2" color="text.secondary">
        {t('General_Table-OfPages', { count: maxPageIndex + 1 })}
      </PTypography>
      <PIconButton
        aria-label={t('General_Table-NextPage')}
        icon={<Iconify icon={ICONS.CHEVRON_RIGHT} width={ICON_SIZE_MAP.smPlus} height={ICON_SIZE_MAP.smPlus} />}
        variant="ghost"
        size="small"
        disabled={page >= maxPageIndex}
        onClick={handleNextPage}
      />
    </Box>
  );
};
